import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Button from '../../atoms/Button';
import PopupOverlay from '../../organisms/Background/PopupOverlay';
import Card from '../Card';
import PanelInputField from '../PanelInputField';
import { accountService } from '../../../services/accountService';
import './index.scss';

const initialForm = { currentPassword: '', newPassword: '', confirmPassword: '' };

const ChangePasswordModal = ({ onClose, onSuccess }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState(initialForm);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const modalTitle = t('profile.passwordModal.title');

  const validate = () => {
    if (!form.currentPassword || !form.newPassword || !form.confirmPassword) {
      return t('profile.passwordModal.errors.missingFields');
    }
    if (form.newPassword !== form.confirmPassword) {
      return t('profile.passwordModal.errors.mismatch');
    }
    if (!form.newPassword.trim()) {
      return t('profile.passwordModal.errors.blank');
    }
    if (form.newPassword.length < 8) {
      return t('profile.passwordModal.errors.length');
    }
    if (form.newPassword === form.currentPassword) {
      return t('profile.passwordModal.errors.sameAsCurrent');
    }
    return null;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    setError(null);
  };

  const handleSubmit = async () => {
    const v = validate();
    if (v) { setError(v); return; }
    try {
      setIsSubmitting(true);
      setError(null);
      await accountService.changePassword({
        currentPassword: form.currentPassword,
        newPassword: form.newPassword
      });
      onSuccess?.();
      onClose();
    } catch (err) {
      const msg = err?.response?.data?.message;
      setError(msg || t('profile.passwordModal.errors.generic'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Render into the global #portal so the fixed overlay escapes the page's
  // stacking context (#content children sit below the fixed mobile header,
  // z-index 50) and paints above it.
  const portalTarget = typeof document !== 'undefined'
    ? document.getElementById('portal')
    : null;

  const overlay = (
    <PopupOverlay
      onClose={onClose}
      closeOnEsc={!isSubmitting}
      closeOnOverlayClick={!isSubmitting}
      aria-label={modalTitle}
    >
      <Card
        className="account-security-card change-password-card"
        title={(
          <strong className="account-security-card__title change-password-card__title">
            {modalTitle}
          </strong>
        )}
        body={
          <div className="account-security-card__content change-password-card__content">
            {error && (
              <div className="account-security-card__error change-password-card__error" role="alert">
                {error}
              </div>
            )}
            <PanelInputField
              label={t('profile.passwordModal.fields.current')}
              id="change-password-current-password"
              type="password"
              name="currentPassword"
              value={form.currentPassword}
              onChange={handleChange}
              disabled={isSubmitting}
              autoComplete="current-password"
            />
            <PanelInputField
              label={t('profile.passwordModal.fields.new')}
              id="change-password-new-password"
              type="password"
              name="newPassword"
              value={form.newPassword}
              onChange={handleChange}
              disabled={isSubmitting}
              autoComplete="new-password"
            />
            <PanelInputField
              label={t('profile.passwordModal.fields.confirm')}
              id="change-password-confirm-password"
              type="password"
              name="confirmPassword"
              value={form.confirmPassword}
              onChange={handleChange}
              disabled={isSubmitting}
              autoComplete="new-password"
            />
            <p className="account-security-card__help change-password-card__help">
              {t('profile.passwordModal.help')}
            </p>
          </div>
        }
        footer={
          <div className="account-security-card__actions change-password-card__actions">
            <Button
              variant="secondary"
              shape="pill"
              className="account-security-card__secondary change-password-card__secondary"
              onClick={onClose}
              disabled={isSubmitting}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              shape="pill"
              className="account-security-card__primary change-password-card__primary"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? t('profile.passwordModal.actions.updating') : t('profile.passwordModal.actions.update')}
            </Button>
          </div>
        }
      />
    </PopupOverlay>
  );

  return portalTarget ? createPortal(overlay, portalTarget) : overlay;
};

ChangePasswordModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
};

ChangePasswordModal.defaultProps = {
  onSuccess: undefined,
};

export default ChangePasswordModal;
